<template>
  <div class="competitors-finder-page-content">
    <template v-if="!resultsLoading">

      <div class="competitors-wrap">
        <div class="progress-container-wrap">

          <progress-bars-heading :app-id="currentApp?.id"
                                 :current-country="currentCountry"
                                 :competitor-count-changed="competitorsCount.count"/>
        </div>

        <div class="options-block-wrap">
          <div class="options-block">
            <div class="input-wrap">
              <form>
                <label>
                  <input type="text"
                         class="search-input"
                         @keydown.enter="searchKeywordsHandler"
                         placeholder="Search by App Name, ID, URL"
                         ref="searchInput"
                         v-model="searchKeywordsInput">
                </label>
              </form>
              <svg-icon class="search-icon"
                        @click="searchKeywordsHandler"
                        icon="search-solid"/>
            </div>
            <data-table-checkbox :checked="screenshotsCheckbox"
                                 @input-checked="screenshotsCheckbox = !screenshotsCheckbox">
              Screenshots
            </data-table-checkbox>
          </div>

          <order-radaso-button :pre-selected="['aso-audit']" />
        </div>

        <div class="competitors-table">
          <div class="progress-wrap"
               v-if="competitorsTabsLoading">
            <progress-indicator>{{ loaderText }}</progress-indicator>
          </div>
          <template v-else>
            <basic-div-table v-if="competitorsComputedArray.length > 0"
                             :headers="headers"
                             :items="competitorsComputedArray"
                             :fixed-width="true"
                             :show-actions="false"
                             :no-pagination="true"
                             :current-country="currentCountry">

              <template v-slot:items="{item}">

                <div class="row-content">
                  <div class="td t-cell w76"
                       :style="tableStyleCombined(tableHeaderItem('logo'))"
                       :class="tableClassCombined(tableHeaderItem('logo'))">
                    <div class="inner-content cursor-pointer"
                         @click="competitorsItemClickHandler(item)">
                      <img v-if="item?.data?.logo"
                           :width="52"
                           :height="52"
                           :src="cropImg(item?.data?.logo, '52x52', currentApp?.store?.key)"
                      />

                      <div v-else class="empty-logo"></div>
                    </div>
                  </div>

                  <div class="td t-cell w350"
                       :style="tableStyleCombined(tableHeaderItem('title'))"
                       :class="tableClassCombined(tableHeaderItem('title'))">
                    <div class="inner-content">
                      <div class="title-block">
                        <a :href="item?.data?.link"
                           class="link"
                           target="_blank">
                          <svg-icon icon="link-icon" class="link-icon"/>
                        </a>
                        <div class="title cursor-pointer" @click="competitorsItemClickHandler(item)">
                          {{ item?.data?.title }}
                        </div>
                      </div>
                      <div class="subtitle-block" v-html="item?.data?.sub_title"></div>

                    </div>
                  </div>

                  <div class="td t-cell w140"
                       :style="tableStyleCombined(tableHeaderItem('tools'))"
                       :class="tableClassCombined(tableHeaderItem('tools'))">
                    <div class="inner-content">
                      <div class="competitors-block">
                        <template v-if="+item?.data?.id !== +currentApp?.id">
                          <change-competitor-state :item="{
                                                     data: {
                                                       id: item?.data?.id,
                                                     },
                                                     competitorType: item?.competitorType,
                                                   }"
                                                   :current-app-id="currentApp?.id"
                                                   @competitor-changed="competitorChanged"
                          />
                        </template>
                      </div>
                    </div>
                  </div>

                  <div class="td t-cell w140"
                       :style="tableStyleCombined(tableHeaderItem('publisher'))"
                       :class="tableClassCombined(tableHeaderItem('publisher'))">
                    <div class="inner-content word-break">
                      {{ item?.data?.developer_name }}
                    </div>
                  </div>

                  <div class="td t-cell w140"
                       :style="tableStyleCombined(tableHeaderItem('categories'))"
                       :class="tableClassCombined(tableHeaderItem('categories'))">
                    <div class="inner-content word-break">
                      {{ item?.data?.categories?.join(', ') }}
                    </div>
                  </div>

                  <div class="td t-cell w76"
                       :style="tableStyleCombined(tableHeaderItem('rate'))"
                       :class="tableClassCombined(tableHeaderItem('rate'))">
                    <div class="inner-content word-break">
                      <div class="rating-block">
                        <svg-icon icon="star" class="rating-icon"/>
                        <strong class="rating">{{ item?.data?.rating_num }}</strong>
                      </div>
                    </div>
                  </div>

                  <div class="td t-cell w100"
                       :style="tableStyleCombined(tableHeaderItem('ratings'))"
                       :class="tableClassCombined(tableHeaderItem('ratings'))">
                    <div class="inner-content word-break">
                      <div class="voted">
                        {{ item?.data?.votes_num }}
                      </div>
                    </div>
                  </div>

                  <div class="td t-cell w100"
                       v-if="showInstalls"
                       :style="tableStyleCombined(tableHeaderItem('installs'))"
                       :class="tableClassCombined(tableHeaderItem('installs'))">
                    <div class="inner-content word-break">
                      {{ shortenDigits(item?.data?.installs) }}
                    </div>
                  </div>

                  <div class="td t-cell w120"
                       v-if="showInstalls"
                       :style="tableStyleCombined(tableHeaderItem('revenue'))"
                       :class="tableClassCombined(tableHeaderItem('revenue'))">
                    <div class="inner-content word-break">
                      {{ shortenDigits(item?.data?.revenue) }}
                    </div>
                  </div>

                  <div class="td t-cell w140"
                       v-if="showInstalls"
                       :style="tableStyleCombined(tableHeaderItem('impressions'))"
                       :class="tableClassCombined(tableHeaderItem('impressions'))">
                    <div class="inner-content word-break">
                      <template v-if="isSummaryImpressionsLoaded">
                        {{ item?.data?.impressions ? formatNumber(item?.data?.impressions) : 0 }}
                      </template>
                      <template v-else>
                        <preloader-table-small :loader-size="'25px'"></preloader-table-small>
                      </template>
                    </div>
                  </div>
                </div>

                <div class="full-width pb-16 pt-16 pr-20 pl-20" v-if="screenshotsCheckbox">
                  <screenshots-carousel :app-id="item?.data?.id"
                                        :key="item?.data?.id"
                                        :country-code="currentCountryManual?.code ?? currentCountry?.code"
                                        :store-key="currentApp?.store?.key"
                  />
                </div>
              </template>
            </basic-div-table>
            <template v-else>
              <div class="empty-competitors-block">
                <div class="text">
                  Oh no, there aren't enough keywords to find competitors.<br>
                  Adding more keywords might help you find more competitors who are relevant to your app.
                </div>
                <div class="button-block">
                  <button class="btn btn-default"
                          @click="openKeywordsModalOutside">
                    Add Keywords
                  </button>
                </div>
              </div>
            </template>
          </template>
        </div>

        <div class="pagination-block"
             v-if="!competitorsTabsLoading && liveSearchResults.results?.length > 12">

          <custom-tooltip
                v-if="currentPageCompetitorsMarked < (competitorsComputedArray?.length < 5 ? competitorsComputedArray?.length : 5)"
                width="300px"
                :classes="'cursor-not-allowed'"
                text-align="center">
            <template v-slot:slotTrigger>
              <base-button height="40px"
                           border-radius="4px"
                           :disabled="currentPageCompetitorsMarked < (competitorsComputedArray?.length < 5 ? competitorsComputedArray?.length : 5)"
                           classes="pagination-btn next"
                           @click="competitorsNextBtnClicked">
                <div class="text">Next</div>
                <svg-icon icon="arrow-right"/>
              </base-button>
            </template>
            <template v-slot:slotContent>
              Mark Apps as competitors & non-competitors and we will show next more relevant suggests
            </template>
          </custom-tooltip>
          <template v-else>
            <base-button height="40px"
                         border-radius="4px"
                         v-if="liveSearchResults.results?.length > 12"
                         :disabled="currentPageCompetitorsMarked < (competitorsComputedArray?.length < 5 ? competitorsComputedArray?.length : 5)"
                         classes="pagination-btn next"
                         @click="competitorsNextBtnClicked">
              <div class="text">Next</div>
              <svg-icon icon="arrow-right"/>
            </base-button>
          </template>
        </div>
      </div>
    </template>
    <template v-else>
      <progress-indicator :loaded="resultsLoading">{{ loaderText }}</progress-indicator>
    </template>

    <custom-modal :opened="showCompetitorsNoticeModal"
                  :max-width="728"
                  class-name="find-new-page-modal"
                  @closeModal="closeModal('showCompetitorsNoticeModal')">
      <div class="modal-message">
        <div class="modal-heading medium-heading">
          <div class="modal-subtitle">Despite your best efforts, you have not marked all applications.<br> As you mark
            more applications, our
            intelligence algorithms will be able to assemble a higher quality semantic core.
          </div>
        </div>

        <div class="modal-btn-block">
          <base-button mode="outline"
                       height="42px"
                       width="180px"
                       @click="closeModal('showCompetitorsNoticeModal')">Back to Job
          </base-button>
          <base-button height="42px"
                       width="180px"
                       @click="showNextCompetitors">Show Next Apps
          </base-button>
        </div>
      </div>
    </custom-modal>

    <custom-modal :opened="showAddKeywordsModal"
                  :max-width="728"
                  class-name="find-new-page-modal"
                  @closeModal="closeModal('showAddKeywordsModal')">
      <div class="modal-message">
        <div class="modal-heading medium-heading">
          <h3>Please enter 3-5 most relevant keywords, to start
            competitors research</h3>
        </div>
        <textarea-with-chips :chips-array="addedKeywordsArray"
                             :source-array="[]"
                             @input-changed="keywordsChipsInputChanged"
                             @chip-added="addChips($event, 'addedKeywordsArray', 'suggestedKeywords')"
                             @chip-deleted="deleteChips($event, 'addedKeywordsArray', 'suggestedKeywords')"></textarea-with-chips>

        <div class="modal-btn-block">
          <base-button height="42px"
                       width="180px"
                       :disabled="setDisabledKeywordsButton(addedKeywordsArray, keywordsChipsInput)"
                       :class="{gray: setDisabledKeywordsButton(addedKeywordsArray, keywordsChipsInput)}"
                       @click="saveAddedKeywordsClicked">Save and continue
          </base-button>

          <base-button v-if="addedKeywordsArray?.length > 1"
                       mode="outline"
                       height="42px"
                       width="180px"
                       @click="deleteAllAddedKeywords('addedKeywordsArray', 'suggestedKeywords')">
            Delete All
          </base-button>
        </div>
      </div>

    </custom-modal>

    <custom-modal :opened="showSecondaryAddKeywordsModal"
                  :max-width="728"
                  class-name="find-new-page-modal"
                  @closeModal="closeModal('showSecondaryAddKeywordsModal')">
      <div class="modal-message">
        <div class="modal-heading medium-heading">
          <h3>Oh no, there aren't enough keywords to find competitors.<br>
            Adding more keywords might help you find more competitors who are relevant to your app.</h3>
        </div>
        <textarea-with-chips :chips-array="addedSecondaryKeywordsArray"
                             :source-array="addedSecondaryKeywordsArray"
                             @input-changed="keywordsChipsInputChanged"
                             :no-delete-option="true"
                             @chip-added="addChips($event, 'addedSecondaryKeywordsArray', 'suggestedKeywords')"
                             @chip-deleted="deleteChips($event, 'addedSecondaryKeywordsArray', 'suggestedKeywords')"/>
        <div class="modal-btn-block">
          <base-button height="42px"
                       width="180px"
                       :disabled="initialSecondaryKeywordsArrayLength === addedSecondaryKeywordsArray?.length"
                       :class="{gray: initialSecondaryKeywordsArrayLength === addedSecondaryKeywordsArray?.length}"
                       @click="saveAddedSecondaryKeywordsClicked()">
            Save and continue
          </base-button>
        </div>

        <template v-if="recommendedSuggestedKeywordsNotAdded?.length > 0">
          <div class="suggested-keywords-block">
            <div class="suggested-keywords">
              <div class="modal-heading medium-heading">
                <h3 class="">Pay attention to these keywords</h3>
              </div>

              <custom-chips
                    @chip-deleted="deleteSuggestedChips(chip)"
                    @chip-added="addChips(chip, 'addedSecondaryKeywordsArray', 'recommendedSuggestedKeywords')"
                    v-for="(chip, idx) in recommendedSuggestedKeywordsNotAdded"
                    :selectable="true"
                    :is-selected="false"
                    :is-frozen="chip.frozen"
                    :key="idx"
                    :amount="+chip?.sap">{{ chip.keyword }}
              </custom-chips>
            </div>

            <div class="modal-btn-block">
              <base-button @click="addAllRecommendedSuggestedKeywords">Add All
              </base-button>
              <!--              <base-button v-if="arrayHasSelectedItems('addedAutoSuggestedKeywordsArray')"-->
              <!--                           @click="addRecommendedSuggestedKeywords('selected')"-->
              <!--                           mode="outline">Add selected-->
              <!--              </base-button>-->
            </div>
          </div>
        </template>
      </div>

    </custom-modal>

    <custom-modal :opened="showLiveSearchModal"
                  :max-width="660"
                  :immutable-width="true"
                  class-name="live-search-modal"
                  @closeModal="liveSearchModalClosed">
      <live-search-modal :current-country-code="currentKeywordCountryCode"
                         :current-country-name="currentKeywordCountryName"
                         :current-country-id="currentKeywordCountryId"
                         :current-keyword-item="searchKeywordsInput"
                         @search-competitor-changed="modalCompetitorChanged"/>
    </custom-modal>

    <details-modal v-if="showAppDetailsModal"
                   :opened="showAppDetailsModal"
                   :app-data="clickedItem"
                   @competitor-changed="modalCompetitorChanged"
                   @modal-closed="showAppDetailsModal = !showAppDetailsModal"/>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import ProgressBar from "@/components/UI/ProgressBar";
import ProgressIndicator from "@/components/UI/ProgressIndicator";
import CompetitorBillet from "@/views/CompetitorsFinderPage/components/CompetitorBillet";
import ModalAppUpperDetails from "@/views/CompetitorsFinderPage/components/ModalAppUpperDetails";
import ModalAppScreenshots from "@/views/CompetitorsFinderPage/components/ModalAppScreenshots";
import ModalAppMetrics from "@/views/CompetitorsFinderPage/components/ModalAppMetrics";
import ModalAppMetadata from "@/views/CompetitorsFinderPage/components/ModalAppMetadata";
import RankingsTable from "@/views/CompetitorsFinderPage/components/RankingsTable";
import {competitorsCategoryRankingTableConfig} from "@/configs/page-tables-configs/competitors-category-ranking.js";
import {competitorsRankingKeywordsTableConfig} from "@/configs/page-tables-configs/competitors-ranking-keywords.js";
import CustomTextareaWithChips from "@/components/Forms/CustomTextareaWithChips";
import {cropImage, formatDate, formatNumber, shortenDigits} from "@/utils/utils";
import CustomChips from "@/components/Forms/CustomChips";
import {httpRequest} from "@/api";
import DetailsModal from "@/components/AppDetailsComponent/DetailsModal/index.vue";
import ProgressBarsHeading from "@/views/CompetitorsFinderPage/components/ProgressBarsHeading/index.vue";
import BasicDataTable from "@/components/DataTables/BasicDataTable/index.vue";
import CustomDivDataTable from "@/components/DataTables/CustomDivDataTable/index.vue";
import BasicSearchInput from "@/components/UI/BasicSearchInput/index.vue";
import LiveSearchModal from "@/components/Modals/LiveSearchModal/index.vue";
import TableCheckbox from "@/components/UI/TableCheckbox/index.vue";
import ScreenshotsComponent from "@/views/CompetitorsFinderPage/components/ScreenshotsComponent/index.vue";
import ScreenshotsCarousel from "@/views/CompetitorsFinderPage/components/ScreenshotsCarousel/index.vue";
import competitorState from "@/api/modules/competitor-state";
import ChangeCompetitorState from "@/components/Billets/ChangeCompetitorState/index.vue";
import OrderRadasoButton from "@/components/OrderRadaso/Button/index.vue";
import SvgIcon from "@/components/UI/SvgIcon/index.vue";
import PreloaderTableSmall from "@/components/UI/PreloaderTableSmall/index.vue";

export default {
  name: "Finder",
  components: {
    PreloaderTableSmall,
    SvgIcon, OrderRadasoButton,
    ChangeCompetitorState,
    ScreenshotsCarousel,
    'basic-search-input': BasicSearchInput,
    'progress-indicator': ProgressIndicator,
    'competitor-billet': CompetitorBillet,
    'modal-app-upper-details': ModalAppUpperDetails,
    'modal-app-metrics': ModalAppMetrics,
    'modal-app-screenshots': ModalAppScreenshots,
    'modal-app-meta-data': ModalAppMetadata,
    'rankings-table': RankingsTable,
    'textarea-with-chips': CustomTextareaWithChips,
    'custom-chips': CustomChips,
    'basic-data-table': BasicDataTable,
    'basic-div-table': CustomDivDataTable,
    'live-search-modal': LiveSearchModal,
    'data-table-checkbox': TableCheckbox,
    ProgressBar,
    DetailsModal,
    ProgressBarsHeading,
    ScreenshotsComponent,
  },
  data() {
    return {
      loaderText: '',
      currentKeywordCountryName: '',
      currentKeywordCountryCode: 'US',
      currentKeywordCountryId: null,
      resultsLoading: false,
      competitorsTabsLoading: false,
      showAddKeywordsModal: false,
      showLiveSearchModal: false,
      showSecondaryAddKeywordsModal: false,
      showCompetitorsNoticeModal: false,
      showAppDetailsModal: false,
      appCategoryRankingHeaders: competitorsCategoryRankingTableConfig.headers,
      appRankingKeywordsHeaders: competitorsRankingKeywordsTableConfig.headers,
      addedKeywordsArray: [],
      addedSecondaryKeywordsArray: [],
      initialSecondaryKeywordsArrayLength: 0,
      suggestedKeywords: [],
      currentCompetitorsTab: 'all',
      currentPage: 1,
      scrolledInitialCount: 48,
      isScrolledToBottom: false,
      keywordsChipsInput: '',
      modalData: {
        topAppData: {},
        topLocalesData: [],
        appInfoData: {},
        appVersionHistoryData: {},
        appMetricsData: {},
        appScreenshotsData: [],
        appMetaData: [],
        appRankingKeywordsData: [],
        appCategoryRankingData: [],
        fetchFilterLocales: [],
        filterLocalesArray: [],
        filterCountriesArray: [],
      },
      categoryRankingsLoaded: false,
      rankingKeywordsLoaded: false,
      metadataLoaded: false,
      metricsDataLoaded: false,
      screenshotsDataLoaded: false,
      appUpperDetailsLoaded: false,
      appInfoDataLoaded: false,
      historyDataLoaded: false,
      tabDropdownOpened: false,
      keywordsTrackingCount: 0,
      currentPageCompetitorsAmount: 0,
      totalPages: 0,
      competitorsCount: 0,
      countryMainLocale: {},
      tabClickCounterStart: null,
      currentCompetitorType: '',
      currentCompetitorId: null,
      currentCompetitorStore: '',
      addedAutoSuggestedKeywordsArray: [],
      recommendedSuggestedKeywords: [],
      recommendedSuggestedKeywordsAddedAll: false,
      clickedItem: {},
      searchKeywordsInput: '',
      screenshotsCheckbox: false,
      competitorClickedCount: 0,
      isSummaryImpressionsLoaded: false,
    }
  },
  async mounted() {
    this.currentKeywordCountryName = this.currentCountry?.name;
    this.currentKeywordCountryCode = this.currentCountry?.code;
    this.currentKeywordCountryId = this.currentCountry?.id;
    this.resultsLoading = true;

    await this.fetchCompetitorsCount();
    await this.fetchDataCounts();

    if (this.dataCounts?.trackingKeywords?.total?.value >= 3) {
      await this.fetchCompetitorsData();
      if (this.liveSearchResults.results?.length < 12) {
        this.showAddKeywordsFirstModal();
      }
    } else {
      this.showAddKeywordsFirstModal();
    }

    this.resultsLoading = false;

    await competitorState.setEmptyCompetitors();
  },
  methods: {
    formatNumber,
    shortenDigits,
    async fetchCompetitorsData(appId, countryId) {
      this.competitorsTabsLoading = true;
      this.screenshotsDataLoaded = false;
      const currentAppId = appId ?? this.currentApp.id;
      const currentCountryId = countryId ?? this.currentKeywordCountryCode;
      const url = '?country_code=' + currentCountryId + '&app_id=' + currentAppId;
      const result = await httpRequest('GET', process.env.VUE_APP_API_URL + this.$ApiUrls.competirorsFinder.COMPETITORS_IN_FINDER + url);

      let tempArray = [];

      if (result?.isArray) {
        tempArray = [...result];
      } else if (Object.values(result)) {
        tempArray = Object.values(result);
      }

      let competitorsArray = tempArray?.map(item => {
        return {
          data: {
            id: item?.application?.id,
            origin_id: item?.application?.origin_id,
            logo: item?.application?.logo,
            title: item?.application?.title,
            sub_title: item?.application?.sub_title,
            developer_name: item?.application?.publisher?.name,
            categories: [item?.application?.category],
            rating_num: item?.application?.rating,
            power: item?.power,
            votes_num: shortenDigits(item?.application?.voted),
            store: item?.application?.store?.store_key,
            link: item?.application?.store?.app_url,
            installs: item?.application?.installs,
            revenue: item?.application?.revenue,
          }
        }
      });
      this.$store.dispatch('keywordsTracking/SET_LIVE_SEARCH_RESULTS', {
        results: competitorsArray
      });

      this.competitorsTabsLoading = false;
      this.screenshotsDataLoaded = true;
    },
    async fetchCompetitorsSummaryImpressions () {
      this.isSummaryImpressionsLoaded = false;
      const appIds = this.competitorsComputedArray.map(item => item.data.id);
      const appIdsParams = new URLSearchParams();
      appIds.forEach(id => appIdsParams.append('app_ids[]', id));
      const params = '?country_code=' + this.currentKeywordCountryCode + `&${appIdsParams.toString()}`;
      const result = await httpRequest('GET', process.env.VUE_APP_API_URL + this.$ApiUrls.reports.GET_SUMMARY_IMPRESSIONS + params);

      if (Object.keys(result).length > 0 ) {
        for (let key in this.liveSearchResults.results) {
          const appOriginId = this.liveSearchResults.results[key].data.origin_id;
          this.liveSearchResults.results[key].data.impressions = result[appOriginId] !== undefined
              ? result[appOriginId]
              : null;
        }

        this.isSummaryImpressionsLoaded = true;
      }
    },
    async competitorsItemClickHandler(item) {
      this.clickedItem = {
        id: item.data.id,
        title: item.data.title,
        store: item.data.store,
      };

      this.showAppDetailsModal = true;
    },
    async modalCompetitorChanged(obj) {
      const {type, id, competitorType} = obj;
      let formattedType = type;

      if (type === 'non-competitor' || type === 'nonCompetitor') {
        formattedType = 'nonCompetitor';
      }

      await this.competitorChanged(formattedType, id, competitorType);
    },
    async competitorChanged(newType, id, oldType = null) {
      let newCounter = await competitorState.competitorChanged(
          newType,
          id,
          oldType,
          this.currentCountry?.id,
      );
      this.competitorClickedCount = this.competitorClickedCount + newCounter;
      this.competitorsCount = {
        count: this.competitorsCount.count + newCounter
      };
    },
    showAddKeywordsFirstModal() {
      this.showAddKeywordsModal = true;
    },
    competitorsNextBtnClicked() {
      if ((this.competitorsComputedArray?.length - this.currentPageCompetitorsMarked) >= 4) {
        this.showCompetitorsNoticeModal = true;
      } else {
        this.showNextCompetitors();
      }
    },
    async showNextCompetitors() {
      this.showCompetitorsNoticeModal = false;

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });

      if (this.currentPage < 2 && this.currentPage < this.totalPages) {
        this.currentPage++;
      } else {
        this.resultsLoading = true;
        this.currentPage = 1;
        await this.fetchCompetitorsData();
        this.resultsLoading = false;
      }
    },
    //add/delete keywords
    keywordsChipsInputChanged(e) {
      this.keywordsChipsInput = e;
    },
    addChips(chip, textAreaArray, suggestedArray) {
      this[textAreaArray].push({
        ...chip,
        name: chip?.keyword ?? chip?.name,
      });
      const selectedChipsIdx = this[suggestedArray].findIndex(keyword => keyword.name === chip.name);
      if (selectedChipsIdx >= 0) {
        const tempChipsArray = [...this[suggestedArray]];
        tempChipsArray[selectedChipsIdx].selected = true;
        this[suggestedArray] = [...tempChipsArray];
      }

      if (this.showAddKeywordsModal) {
        this.addedSecondaryKeywordsArray = [...this.addedKeywordsArray];
      }

      this.keywordsChipsInput = '';
    },
    deleteChips(chip, textAreaArray, suggestedArray) {
      const selectedChipsIdx = this[textAreaArray].findIndex(keyword => keyword?.name === chip?.name);
      const selectedSuggestedChipsIdx = this[suggestedArray].findIndex(keyword => keyword?.name === chip?.name);
      if (selectedChipsIdx >= 0) {
        this[textAreaArray].splice(selectedChipsIdx, 1);
      }

      if (selectedSuggestedChipsIdx >= 0) {
        const tempChipsArray = [...this[suggestedArray]];
        tempChipsArray[selectedSuggestedChipsIdx].selected = false;
        this[suggestedArray] = [...tempChipsArray];
      }
    },
    async saveAddedKeywordsClicked() {
      this.keywordsLoaded = false;
      if (this.keywordsChipsInput.length > 0) {
        if (!this.addedKeywordsArray.some(e => e.name === this.keywordsChipsInput)) {
          this.addedKeywordsArray.push({
            name: this.keywordsChipsInput,
            keyword: this.keywordsChipsInput,
            selected: true
          });
        }
      }

      if (this.addedKeywordsArray.length >= 3) {
        this.addedSecondaryKeywordsArray = this.addedKeywordsArray?.map(item => {
          return {
            name: item?.name,
            selectable: false,
            selected: true,
            frozen: true,
          }
        });
        await this.saveAddedKeywords('addedKeywordsArray');
        this.initialSecondaryKeywordsArrayLength = this.addedSecondaryKeywordsArray?.length;
        this.addedKeywordsArray = [];
        this.keywordsChipsInput = '';
        this.keywordsLoaded = true;

        this.closeModal('showAddKeywordsModal');

        await this.fetchCompetitorsData();
        if (this.liveSearchResults.results?.length < 12) {
          this.openModal('showSecondaryAddKeywordsModal');

          await this.fetchRecommendedSuggestKeywords();
        }
      }
    },
    async saveAddedSecondaryKeywordsClicked() {
      this.keywordsLoaded = false;
      if (this.keywordsChipsInput.length > 0) {
        if (!this.addedSecondaryKeywordsArray.some(e => e.name === this.keywordsChipsInput)) {
          this.addedSecondaryKeywordsArray.push({
            name: this.keywordsChipsInput,
            keyword: this.keywordsChipsInput,
            selected: true
          });
        }
      }

      await this.saveAddedKeywords('addedSecondaryKeywordsArray');

      this.keywordsChipsInput = '';
      this.keywordsLoaded = true;
      this.initialSecondaryKeywordsArrayLength = this.addedSecondaryKeywordsArray?.length;

      this.closeModal('showSecondaryAddKeywordsModal');

      await this.fetchCompetitorsData();
      if (this.liveSearchResults.results?.length < 12) {
        this.openModal('showSecondaryAddKeywordsModal');
      }
    },
    async saveAddedKeywords(keywordsArray, mode = 'string') {
      let preparedArray = mode === 'string' ? this[keywordsArray] : keywordsArray;
      const currentAppId = this.currentApp.id;

      if (currentAppId && preparedArray.length > 0) {
        let formData = new FormData();
        formData.append("country_code", this.currentKeywordCountryCode);
        formData.append("application_id", currentAppId);

        preparedArray.forEach((keyword, idx) => {
          formData.append(`keywords[${idx}]`, keyword.name);
        });

        await httpRequest('POST', process.env.VUE_APP_API_URL + this.$ApiUrls.keywordsTracking.ALL_KEYWORDS, formData);
        preparedArray = [];

        this.$store.dispatch('UPDATE_USER_LIMITS');
        this.$store.dispatch('keywordsTracking/SET_TRACKING_KEYWORDS_COUNT');

        this.resultsLoading = true;
        await this.fetchDataCounts();
        this.resultsLoading = false;
      }
    },
    deleteAllAddedKeywords(textAreaArray, suggestedArray) {
      this[textAreaArray] = [];
      const tempChipsArray = this[suggestedArray].map(item => {
        return {
          ...item,
          selected: false
        }
      });
      this[suggestedArray] = [...tempChipsArray];

      this.$store.dispatch('keywordsTracking/SET_TRACKING_KEYWORDS_COUNT');
    },
    async deleteSuggestedChips(chip) {
      this.deleteChips(chip, 'addedAutoSuggestedKeywordsArray', 'recommendedSuggestedKeywords');
    },
    constructLocalizationRows(localizationsObj, fullLocalesArray) {
      const titles = [...localizationsObj?.titles];
      const subTitles = [...localizationsObj?.sub_titles];
      const descriptions = [...localizationsObj?.descriptions];
      let summaryLocales = [];
      let localeRows = [];
      let unitedBundles = [];

      titles.forEach(title => {
        summaryLocales = [...summaryLocales, ...title.locales];
      });

      summaryLocales.forEach(locale => {
        const matchTitle = titles.filter(title => title.locales.includes(locale));
        const matchSubtitle = subTitles.filter(subtitle => subtitle.locales.includes(locale));
        const matchDescription = descriptions.filter(description => description.locales.includes(locale));

        localeRows.push({
          title: matchTitle[0]?.text,
          subTitle: matchSubtitle[0]?.text,
          description: matchDescription[0]?.text,
          locale: locale,
        });
      });

      let matchElement = {};

      localeRows.forEach(locale => {
        const findedMatchIdx = unitedBundles.findIndex(item => item.title === locale.title && item.subTitle === locale.subTitle);
        if (findedMatchIdx < 0) {
          matchElement = {...locale};
          let bundle = [];
          let localeArray = [];

          localeRows.forEach(innerLocale => {
            if (innerLocale.title === matchElement.title && innerLocale.subTitle === matchElement.subTitle) {
              bundle.push(innerLocale);
              localeArray.push(innerLocale.locale);
            }
          });

          let groupedBundles = [];

          bundle.forEach(item => {
            const itemIdx = groupedBundles.findIndex(bundleItem => bundleItem.description === item.description);

            if (itemIdx < 0) {
              groupedBundles.push({
                title: item.title,
                subTitle: item.subTitle,
                description: item.description,
                locale: item.locale,
              });
            } else {
              let groupedBundlesCopy = [...groupedBundles];
              groupedBundlesCopy[itemIdx].locale += ',' + item.locale;
              groupedBundles = [...groupedBundlesCopy];
            }

          });

          const bundleFormattedGroup = groupedBundles.map(bundleItem => {
            let tempItem = {...bundleItem};
            const bundleLocalesArray = tempItem.locale.split(',');
            tempItem.localesString = this.constructLocalesString(fullLocalesArray, bundleLocalesArray);
            tempItem.localesAmount = bundleLocalesArray.length;

            return tempItem;
          });

          const localesString = this.constructLocalesString(fullLocalesArray, localeArray);

          unitedBundles.push({
            title: bundle[0]?.title,
            subTitle: bundle[0]?.subTitle,
            description: bundle[0]?.description,
            descriptionArray: bundleFormattedGroup,
            locales: localeArray,
            localesString: localesString
          });
        }
      });

      return unitedBundles;
    },
    constructLocalesString(fullLocalesArray, localeArray) {
      let localeString = '';
      localeArray.forEach((locale, idx) => {
        let sign = '';
        if (idx !== 0) {
          sign = ', ';
        }
        const splittedCode = locale.split('_');
        let countryExtention = '';

        if (splittedCode?.length > 1) {
          countryExtention = ' (' + this.decodeLocaleItem(this.modalData.filterCountriesArray, splittedCode[1]) + ')';
        }
        localeString += sign + this.decodeLocaleItem(fullLocalesArray, locale) + countryExtention;
      });

      return localeString;
    },
    decodeLocaleItem(fullLocalesArray, code) {
      let localeString = '';
      const localeFullData = fullLocalesArray.find(item => item?.code?.toLowerCase() === code?.toLowerCase());
      if (localeFullData) {
        return localeString = localeFullData?.name;
      } else {
        return code;
      }
    },
    //ExplorerModal components promises bundles
    async fetchCompetitorsCount(appId) {
      const currentAppId = appId ?? this.currentApp.id;
      const competitorsUrl = '?app_id=' + currentAppId;
      this.competitorsCount = await httpRequest('GET', process.env.VUE_APP_API_URL + this.$ApiUrls.user.FETCH_COMPETITORS_COUNT + competitorsUrl + '&is_competitor=1');
    },
    async fetchFilterLocales() {
      const localesData = await httpRequest('GET', process.env.VUE_APP_API_URL + this.$ApiUrls.user.FILTER_LOCALES);
      const countriesData = await httpRequest('GET', process.env.VUE_APP_API_URL + this.$ApiUrls.user.FILTER_COUNTRIES);

      if (localesData?.length > 0 && countriesData?.length > 0) {
        this.modalData.filterLocalesArray = [...localesData];
        this.modalData.filterCountriesArray = [...countriesData];
      }

      const currentCountryMainLocale = countriesData.find(country => country?.code === this.currentCountry?.code);

      if (currentCountryMainLocale?.locales?.main) {
        const localeName = localesData.find(locale => locale.code === currentCountryMainLocale?.locales?.main);
        this.countryMainLocale = {
          code: currentCountryMainLocale?.locales?.main,
          title: localeName?.name,
        };
      } else {
        const firstFoundMainLocale = countriesData.find(country => country?.locales?.main);
        const localeName = localesData.find(locale => locale.code === firstFoundMainLocale);
        this.countryMainLocale = {
          code: firstFoundMainLocale,
          title: localeName?.name,
        };
      }
    },
    formatDate(val) {
      return formatDate(val, 'digits');
    },
    async fetchDataCounts(countryCode) {
      this.$store.dispatch('SET_DATA_COUNTS_LOADER', false);
      await this.$store.dispatch('SET_DATA_COUNTS', {countryCode});
      this.$store.dispatch('SET_DATA_COUNTS_LOADER', true);
    },
    setDisabledKeywordsButton(array, inputStr) {
      if (array?.length >= 3) {
        return false;
      } else if (array?.length === 2 && inputStr !== '') {
        return false;
      } else {
        return true;
      }
    },
    openKeywordsModalOutside() {
      if (this.addedKeywordsArray?.length === 0 && this.addedSecondaryKeywordsArray?.length === 0) {
        this.openModal('showAddKeywordsModal');
      } else if (this.addedKeywordsArray?.length > 0) {
        this.openModal('showAddKeywordsModal');
      } else {
        this.openModal('showSecondaryAddKeywordsModal');
      }
    },
    async fetchRecommendedSuggestKeywords() {
      this.recommendedSuggestedKeywordsAddedAll = false;
      this.recommendedSuggestedKeywords = [];
      this.modalDataLoader = false;
      const urlQuery = '?app_id=' + this.currentApp.id + '&country_code=' + this.currentKeywordCountryCode + '&sort_by=sap&size=10&skip=0';

      const result = await httpRequest('GET', process.env.VUE_APP_API_URL + this.$ApiUrls.recommendedKeywords.RECOMMENDED_KEYWORDS + urlQuery);
      // const result = {"filter_value_limits":{"max_sap":98,"max_daily_impression":null,"max_rank":47,"min_sap":5,"min_daily_impression":null,"min_rank":1},"counts":{"all":{"value":3136,"dynamic":3136},"pending":{"value":3131,"dynamic":3131},"declined":{"value":0,"dynamic":0}},"list":[{"keyword":{"term":"snapchat","is_tracked":false,"is_declined":false,"tracked_id":null},"sap":98,"daily_impression":null,"rank":{"value":7,"dynamic":0},"sources":["competitors"]},{"keyword":{"term":"tinder","is_tracked":true,"is_declined":false,"tracked_id":390},"sap":89,"daily_impression":null,"rank":{"value":1,"dynamic":0},"sources":["meta","suggestions"]},{"keyword":{"term":"grindr","is_tracked":false,"is_declined":false,"tracked_id":null},"sap":86,"daily_impression":null,"rank":{"value":4,"dynamic":0},"sources":["competitors"]},{"keyword":{"term":"kik","is_tracked":false,"is_declined":false,"tracked_id":null},"sap":80,"daily_impression":null,"rank":{"value":7,"dynamic":0},"sources":["competitors"]},{"keyword":{"term":"google chat","is_tracked":false,"is_declined":false,"tracked_id":null},"sap":73,"daily_impression":null,"rank":{"value":null,"dynamic":"out"},"sources":["suggestions"]},{"keyword":{"term":"blk","is_tracked":false,"is_declined":false,"tracked_id":null},"sap":72,"daily_impression":null,"rank":{"value":2,"dynamic":0},"sources":["competitors"]},{"keyword":{"term":"chatgpt","is_tracked":false,"is_declined":false,"tracked_id":null},"sap":72,"daily_impression":null,"rank":{"value":null,"dynamic":"out"},"sources":["suggestions"]},{"keyword":{"term":"yubo","is_tracked":false,"is_declined":false,"tracked_id":null},"sap":71,"daily_impression":null,"rank":{"value":null,"dynamic":"out"},"sources":["competitors"]},{"keyword":{"term":"badoo","is_tracked":false,"is_declined":false,"tracked_id":null},"sap":71,"daily_impression":null,"rank":{"value":2,"dynamic":0},"sources":["competitors"]},{"keyword":{"term":"tagged","is_tracked":false,"is_declined":false,"tracked_id":null},"sap":70,"daily_impression":null,"rank":{"value":7,"dynamic":0},"sources":["competitors"]}]};
      let selected = 0;
      const suggestedKeywordsArray = result?.list.map(suggestion => {
        if (suggestion.selected) {
          selected++;
        }
        return {
          ...suggestion,
          keyword: suggestion?.keyword?.term,
          frozen: suggestion.selected,
        };
      });

      this.recommendedSuggestedKeywords = suggestedKeywordsArray.filter(item => item?.sap > 20);
      this.recommendedSuggestedKeywordsAddedAll = selected === this.recommendedSuggestedKeywords?.length;
      this.modalDataLoader = true;
    },
    arrayHasSelectedItems(array) {
      return this[array]?.filter(item => {
        if (item?.selected && !item?.frozen) {
          return item;
        }
      }).length > 0;
    },
    addAllRecommendedSuggestedKeywords() {
      this.recommendedSuggestedKeywordsNotAdded.forEach(item => {
        this.addChips(item, 'addedSecondaryKeywordsArray', 'recommendedSuggestedKeywords');
      });
    },
    tableHeaderItem(itemName) {
      return this.headers.find(item => item.value === itemName);
    },
    tableClassCombined(tableItem) {
      return [tableItem?.align, tableItem?.headingClass]
    },
    tableStyleCombined(tableItem) {
      return {width: tableItem?.width};
    },
    cropImg(imageUrl, size, store) {
      return cropImage(imageUrl, size, store);
    },
    searchKeywordsHandler(e) {
      e.preventDefault();
      this.openModal('showLiveSearchModal');
    },
    liveSearchModalClosed() {
      this.closeModal('showLiveSearchModal');
      this.searchKeywordsInput = '';
    },
  },
  computed: {
    ...mapGetters([
      'isMobile',
      'pageMeta',
      'currentApp',
      'currentCountry',
      'currentCountryManual',
      'userLimits',
      'promiseSettledData',
      'sidebarIsCollapsed',
      'dataCounts',
      'dataCountsLoaded',
      'userSubscription',
    ]),
    ...mapGetters('keywordsTracking', [
      'liveSearchResults',
      'competitorsResults',
    ]),
    isFreeUser() {
      return this.userSubscription?.plan_name === 'Free';
    },
    showInstalls() {
      return !this.isFreeUser;
    },
    headers() {
      let headers = [
        {
          text: 'Logo',
          sortable: false,
          value: 'logo',
          width: '76px',
          align: 'left',
          headingClass: 't-cell w76',
          intercomAnchor: 'competitors-finder-logo',
        },
        {
          text: 'Application',
          sortable: false,
          value: 'title',
          sortBy: 'title',
          align: 'left',
          width: 'calc(100% - 636px)',
          textWidth: '270px',
          headingClass: 'app-column t-cell w350',
          intercomAnchor: 'competitors-finder-title',
        },
        {
          text: 'Competitor',
          value: 'tools',
          align: 'left',
          largePadding: true,
          sortable: false,
          intercomAnchor: 'competitors-finder-tools',
          headingClass: 't-cell w140',
        },
        {
          text: 'Publisher',
          value: 'publisher',
          sortBy: 'publisher',
          align: 'left',
          sortable: false,
          textWidth: '200px',
          intercomAnchor: 'competitors-finder-publisher',
          headingClass: 't-cell w140',
        },
        {
          text: 'Category',
          value: 'categories',
          sortBy: 'categories',
          align: 'left',
          sortable: false,
          textWidth: '200px',
          intercomAnchor: 'competitors-finder-category',
          headingClass: 't-cell w140',
        },
        {
          text: 'Rate',
          value: 'rate',
          sortBy: 'rate',
          align: 'right',
          sortable: false,
          intercomAnchor: 'competitors-finder-rate',
          headingClass: 't-cell w76',
        },
        {
          text: 'Ratings',
          value: 'ratings',
          sortBy: 'ratings',
          align: 'right',
          sortable: false,
          intercomAnchor: 'competitors-finder-ratings',
          headingClass: 't-cell w100',
        }
      ];
      if (this.showInstalls) {
        headers.push({
          text: 'Installs',
          value: 'installs',
          sortBy: 'installs',
          align: 'right',
          sortable: false,
          intercomAnchor: 'competitors-finder-installs',
          headingClass: 't-cell w100',
          tooltipWidth: '350px',
          tooltipText: {
            firstPart: '<b>Estimated number of app installations</b> in',
            secondPart: 'over the last month.'
          },
        });
        headers.push({
          text: 'Revenue',
          value: 'revenue',
          sortBy: 'revenue',
          align: 'right',
          sortable: false,
          intercomAnchor: 'competitors-finder-revenue',
          headingClass: 't-cell w120',
          tooltipWidth: '350px',
          tooltipText: {
            firstPart: '<b>Estimated revenue</b> generated by the app in',
            secondPart: 'in the last month.'
          },
        });
        headers.push({
          text: 'Impressions',
          value: 'impressions',
          sortBy: 'impressions',
          align: 'right',
          sortable: false,
          intercomAnchor: 'competitors-finder-impressions',
          headingClass: 't-cell w140',
          tooltipWidth: '380px',
          tooltipText: {
            firstPart: '<b>Estimated search impressions</b> over the last 30 days in the',
            secondPart: ''
          },
        });
      }

      return headers;
    },
    competitorsComputedArray() {
      if (this.liveSearchResults.results === undefined) {
        return [];
      }

      const page = this.currentPage;
      const length = this.liveSearchResults.results?.length;
      const perPage = 12;
      this.totalPages = 0;

      if ((length / perPage) > 1 && (length % perPage) > 1) {
        this.totalPages = Math.floor(length / perPage) + 1;
      } else if ((length / perPage) > 1 && (length % perPage) === 0) {
        this.totalPages = length / perPage;
      } else {
        this.totalPages = 1;
      }

      const arrayDuplicate = [...this.liveSearchResults.results];
      let paginatedArray = [];

      if (page < this.totalPages && page === 1) {
        paginatedArray = arrayDuplicate.splice(0, perPage * page);
      } else if (page < this.totalPages && page > 1) {
        paginatedArray = arrayDuplicate.splice(perPage * (page - 1) + 1, perPage);
      } else {
        paginatedArray = arrayDuplicate.splice(perPage * (page - 1), perPage);
      }

      return paginatedArray;
    },
    currentPageCompetitorsMarked() {
      const marked = this.competitorsComputedArray.filter(item => item.competitorType && item.competitorType !== 'all');
      return marked?.length;
    },
    recommendedSuggestedKeywordsNotAdded() {
      return this.recommendedSuggestedKeywords?.filter(item => {
        const itemIsInArray = this.addedSecondaryKeywordsArray?.some(subItem => subItem?.name === item?.keyword);
        if (!itemIsInArray) {
          return item;
        }
      });
    }
  },
  watch: {
    async $route(to, from) {
      this.title = to?.meta?.pageTitle ?? '';
    },
    async currentApp(newVal, oldVal) {
      if (newVal?.id !== oldVal?.id && oldVal?.id) {
        this.currentCompetitorsTab = 'all';
        this.currentKeywordCountryCode = this.currentCountry?.code;
        this.currentKeywordCountryId = this.currentCountry?.id;
        this.currentKeywordCountryName = this.currentCountry?.name;
        this.resultsLoading = true;

        await this.fetchCompetitorsCount(newVal?.id);
        await this.fetchDataCounts(this.currentCountry?.code);
        if (this.dataCounts?.trackingKeywords?.total?.value >= 5) {
          await this.fetchCompetitorsData(newVal?.id, this.currentCountry?.code);
          if (this.liveSearchResults.results?.length < 10) {
            this.showAddKeywordsFirstModal();
          }

        } else {
          this.showAddKeywordsFirstModal();
        }

        this.resultsLoading = false;
      }
    },
    async currentCountryManual(newVal, oldVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        this.currentKeywordCountryCode = newVal?.code;
        this.currentKeywordCountryId = newVal?.id;
        this.currentKeywordCountryName = newVal?.name;
        this.resultsLoading = true;

        await this.fetchCompetitorsCount();
        await this.fetchDataCounts(newVal?.code);

        if (this.dataCounts?.trackingKeywords?.total?.value >= 3) {
          await this.fetchCompetitorsData();
          if (this.liveSearchResults.results?.length < 10) {
            this.showAddKeywordsFirstModal();
          }

        } else {
          this.showAddKeywordsFirstModal();
        }

        this.resultsLoading = false;
      }
    },
    async competitorsComputedArray(newValue, oldValue) {
      const oldValueAppIds = oldValue.map(item => item.data.id);
      const newValueAppIds = newValue.map(item => item.data.id);

      if (JSON.stringify(oldValueAppIds) !== JSON.stringify(newValueAppIds)) {
        await this.fetchCompetitorsSummaryImpressions();
      } else {
        newValue.forEach((item, index) => {
          if (oldValue[index]?.data?.impressions !== undefined) {
            item.data.impressions = oldValue[index].data.impressions;
          }
        });
      }
    }
  },
}
</script>
<style lang="scss">
  .competitors-finder-page-content {
    .options-block-wrap {
      display: flex;
      justify-content: space-between;
      align-items: center;

      @media only screen and (max-width: 767px) {
        flex-wrap: wrap;

        .order-radaso-button {
          margin-left: 8px;
        }
      }

      .order-radaso-button {
        margin-bottom: 24px;
      }
    }
  }
</style>
